<template>
  <div class="data-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="data-container">
      <div class="data-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-bell title-icon"></i>
              Lotes vencidos
            </h1>
            <p class="page-subtitle">
              Gerencie e monitore todos os lotes vencidos de medicamentos e
              produtos
            </p>
          </div>
        </div>
      </div>

      <div class="lots-inventory-modern">
        <!-- Main Content -->
        <div class="lots-content">
          <!-- Filters Section -->
          <div class="filters-section">
            <div class="filters-card">
              <div class="filters-header">
                <h3 class="filters-title">
                  <i class="fas fa-filter mr-2"></i>
                  Filtros de Pesquisa
                </h3>
              </div>
              <div class="filters-grid">
                <!-- Residente -->
                <div class="filter-group">
                  <label class="filter-label">
                    <i class="fas fa-user filter-icon"></i>
                    Titular
                  </label>
                  <b-form-select
                    v-model="resident_id"
                    :options="residents"
                    value-field="id"
                    text-field="name"
                    @change="paginate"
                    class="modern-select"
                  />
                </div>

                <!-- Medicamento/Produto -->
                <div class="filter-group">
                  <label class="filter-label">
                    <i class="fas fa-search filter-icon"></i>
                    Medicamento/Produto
                  </label>
                  <b-input
                    v-model="search"
                    @input="handleSearch"
                    placeholder="Digite o nome"
                    class="modern-input"
                  />
                </div>

                <!-- Lote -->
                <div class="filter-group">
                  <label class="filter-label">
                    <i class="fas fa-barcode filter-icon"></i>
                    Lote
                  </label>
                  <b-input
                    v-model="lot"
                    @input="handleSearch"
                    placeholder="Número do lote"
                    class="modern-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Table Section -->
          <div class="table-section">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <!-- No Results -->
              <div v-show="lots.length == 0" class="no-results">
                <i class="fas fa-search no-results-icon"></i>
                <h3 class="no-results-title">Nenhum lote encontrado</h3>
                <p class="no-results-text">
                  Tente ajustar os filtros para encontrar resultados
                </p>
              </div>

              <!-- Data Table -->
              <div v-show="lots.length > 0" class="table-card">
                <v-data-table
                  dense
                  :headers="headers"
                  :items="lots"
                  :search="search"
                  item-key="id/stock_item_id"
                  :footer-props="{
                    'items-per-page-options': [25, 50, 75, 100],
                    'items-per-page-text': 'Mostrar',
                  }"
                  :options.sync="options"
                  :server-items-length="total"
                  class="modern-table"
                  :no-data-text="'Nenhum lote encontrado'"
                  :loading-text="'Carregando lotes...'"
                >
                  <template #[`item.name`]="{ item }">
                    <div class="product-name-cell">
                      <div class="product-name">
                        {{ item.name ? item.name : "NÃO VINCULADO" }}
                      </div>
                      <div v-if="!item.name" class="unlinked-badge">
                        Item não vinculado
                      </div>
                    </div>
                  </template>

                  <template #[`item.lot`]="{ item }">
                    <div class="lot-cell">
                      <span v-if="item.lot" class="lot-number">{{
                        item.lot
                      }}</span>
                      <span v-else class="not-informed">NÃO INFORMADO</span>
                    </div>
                  </template>

                  <template #[`item.entry_date`]="{ item }">
                    <div class="date-cell">
                      {{ formatDate(item.entry_date, false) }}
                    </div>
                  </template>

                  <template #[`item.due_date`]="{ item }">
                    <div class="date-cell">
                      <span
                        v-if="item.type == 'MEDICINE'"
                        class="medicine-date"
                      >
                        {{ moment(item.due_date).format("MM/YYYY") }}
                      </span>
                      <span
                        v-else-if="item.type == 'PRODUCT'"
                        class="product-date"
                      >
                        {{
                          item.due_date
                            ? moment(item.due_date).format("DD/MM/YYYY")
                            : "NÃO INFORMADO"
                        }}
                      </span>
                      <span v-else class="unlinked-date">
                        SEM VÍNCULO <small>(Excluir)</small>
                      </span>
                    </div>
                  </template>

                  <template #[`item.type`]="{ item }">
                    <div class="type-cell">
                      <span
                        v-if="item.type == 'MEDICINE'"
                        class="type-badge medicine-type"
                      >
                        MEDICAMENTO
                      </span>
                      <span
                        v-else-if="item.type == 'PRODUCT'"
                        class="type-badge product-type"
                      >
                        PRODUTO
                      </span>
                      <span v-else class="type-badge unlinked-type">
                        SEM VÍNCULO <small>(Excluir)</small>
                      </span>
                    </div>
                  </template>

                  <template #[`item.description`]="{ item }">
                    <div class="description-cell">
                      {{ item.description ? item.description : "--" }}
                    </div>
                  </template>

                  <template #[`item.defeated`]="{ item }">
                    <div v-if="item.defeated" class="alert-cell">
                      <div class="expired-alert">Vencido</div>
                    </div>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <!-- Modals -->
                    <b-modal
                      no-close-on-backdrop
                      size="md"
                      hide-footer
                      :id="`view_lot-${item.id}`"
                      title="EDITAR LOTE"
                      modal-class="modern-modal"
                    >
                      <template v-slot:modal-title>
                        <div class="modal-title-modern">
                          <i class="fas fa-edit mr-2"></i>
                          Editar Lote
                        </div>
                      </template>
                      <view-lot :objLot="item" @updated="paginate" />
                    </b-modal>

                    <b-modal
                      no-close-on-backdrop
                      size="md"
                      hide-footer
                      :id="`view_lot_owner-${item.id}`"
                      title="TITULAR(ES) DO LOTE"
                      modal-class="modern-modal"
                    >
                      <template v-slot:modal-title>
                        <div class="modal-title-modern">
                          <i class="fas fa-users mr-2"></i>
                          Titular(es) do Lote
                        </div>
                      </template>
                      <view-lot-owner :objLot="item" @deleted="paginate" />
                    </b-modal>

                    <!-- Action Buttons -->
                    <div class="actions-cell2">
                      <v-btn
                        small
                        @click="handleModalViewLotOwners(item)"
                        class="btn-action-owners"
                      >
                        <i class="fas fa-users mr-1"></i>
                        Titulares
                      </v-btn>
                      <v-btn
                        v-if="false"
                        small
                        @click="handleModalViewLot(item)"
                        class="btn-action-edit"
                      >
                        <i class="fas fa-edit mr-1"></i>
                        Editar
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </b-overlay>
          </div>

          <!-- Error Messages -->
          <ErrorsMessages :errors="errors" v-if="errors.length > 0" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";

import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";

import ErrorsMessages from "../templates/Components/ErrorsMessages";

import ViewLot from "../templates/Stock/items/lots/view-lot";

import ViewLotOwner from "../templates/Stock/items/lots/view-lot-owner";

import routes from "../routes";

import api from "../services/api";

import { formatDate } from "../lib/format";

import moment from "moment";

export default {
  name: "ListTypeAlertStockLot",

  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    ViewLot,
    ViewLotOwner,
  },

  data() {
    return {
      total: 0,
      options: {},

      errors: [],
      loading: false,

      resident_id: "",

      search: "",

      is_product: false,

      lot: "",

      due_date: moment().format("YYYY-MM-DD"),

      headers: [
        { text: "PRODUTO/MEDICAMENTO", value: "name", sortable: false },
        { text: "LOTE", value: "lot", sortable: false },
        { text: "ENTRADA", value: "entry_date", sortable: false },
        { text: "VENCIMENTO", value: "due_date", sortable: false },
        { text: "TIPO", value: "type", sortable: false },
        { text: "DESCRIÇÃO", value: "description", sortable: false },
        { text: "STATUS", value: "defeated", sortable: false },
        {
          text: "AÇÕES",
          value: "actions",
          align: "end",
          sortable: false,
          width: "200px",
        },
      ],
      lots: [],
      raw_residents: [],

      objLot: null,
    };
  },

  computed: {
    residents() {
      return [
        { id: "", name: "TODOS" },
        { id: "rest_home", name: "EMPRESA" },
        ...this.raw_residents,
      ];
    },
  },

  watch: {
    options: {
      handler() {
        this.paginate();
      },
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    moment,
    formatDate,

    handleToBack: () => routes.go(-1),

    handleSearch() {
      this.options.page = 1;
      this.paginate();
    },

    handleModalViewLot(item) {
      this.$bvModal.show(`view_lot-${item.id}`);
    },

    handleModalViewLotOwners(item) {
      this.$bvModal.show(`view_lot_owner-${item.id}`);
    },

    async retrieve() {
      await this.getResidents();
    },

    async getResidents() {
      try {
        const response = await api.get("/rest-home/active-residents");
        const { error, errors, residents } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.raw_residents = residents;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async paginate() {
      try {
        this.loading = true;

        let { search, lot, due_date, resident_id, is_product } = this;

        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        const params = {
          search,
          lot,
          due_date,
          page,
          itemsPerPage,
          sortBy,
          sortDesc,
          resident_id,
          is_product,
        };

        const response = await api.get("/rest-home/stock/items/lots", {
          params,
        });

        const { error, errors, lots, total } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.lots = lots;
          this.total = total;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.data-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.data-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.lots-inventory-modern {
  background: transparent;
}

/* Content Section */
.lots-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Section */
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 24px;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
}

.section-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 20px;
}

.section-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.filters-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.filters-title i {
  color: #0f65b0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-input,
.modern-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-input:focus,
.modern-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Table Section */
.table-section {
  margin-bottom: 24px;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 300px;
}

.no-results {
  background: white;
  border-radius: 16px;
  padding: 60px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Table Cell Styling */
.product-name-cell {
  padding: 8px 0;
}

.product-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

.unlinked-badge {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
}

.lot-cell {
  padding: 8px 0;
}

.lot-number {
  font-weight: 500;
  color: #1e293b;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 13px;
}

.not-informed {
  color: #6b7280;
  font-style: italic;
  font-size: 13px;
}

.date-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #374151;
}

.medicine-date {
  color: #059669;
  font-weight: 500;
}

.product-date {
  color: #0f65b0;
  font-weight: 500;
}

.unlinked-date {
  color: #dc2626;
  font-weight: 500;
}

.type-cell {
  padding: 8px 0;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.medicine-type {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.product-type {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.unlinked-type {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.description-cell {
  color: #374151;
  font-size: 13px;
}

.alert-cell {
  padding: 8px 0;
}

.expired-alert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #f59e0b;
}

.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action-owners {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-owners:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4) !important;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* Modal Styling */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .content-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .filters-card {
    border-radius: 12px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }

  .btn-action-owners,
  .btn-action-edit {
    width: 100% !important;
    justify-content: center !important;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 18px;
    flex-direction: column;
    gap: 4px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .filters-header {
    padding: 12px 16px;
  }

  .filters-title {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.modern-modal >>> .modal-body {
  padding: 24px !important;
}
</style>